// Angular imports
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChildren, QueryList } from '@angular/core';

// Import rxjs
import { Subject, takeUntil } from 'rxjs';

// Import services
import { UndoService } from '../../../services/undo.service';
import { ApplicationContextService } from '../../../services/application-context.service';

// Import components
import { CellComponent } from '../../cell/cell.component';

// Import column and selection types
import { CalculationMetaApi, CalculationTailApi, ColumnMap } from '../../../types';
import { Rect } from '../../types';
import { CalculationSelection, ViewModelLineType, ViewModelLine } from 'src/app/state-management/calculation-state';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state-management/app-state';
import { updateTailLineUi } from 'src/app/state-management/calculation-actions';
import { selectCalculationMeta } from 'src/app/state-management/calculation-selectors';
import * as CalculationActions from 'src/app/state-management/calculation-actions';


// Define types
type TailLineType = '€' | '%' | 't';

@Component({
    selector: 'tail-line-row',
    templateUrl: './line-row.component.html',
    styleUrls: ['./line-row.component.scss'],
    standalone: false
})
export class TailRowComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();
    private meta: CalculationMetaApi;

    public _tailLine: CalculationTailApi;
    public _line: ViewModelLine;


    @Input() public set line(value: ViewModelLine){
        this._line = value;
        this._tailLine = JSON.parse(JSON.stringify(value.line));
    };
    @Input() public index: number;
    @Input() public columns: ColumnMap;

    @Output() public change = new EventEmitter<ViewModelLine>();

    // Selection
    @Input() public selection: CalculationSelection;
    @Output() public selectionChange = new EventEmitter<Selection>();

    // Events for moving selection
    @Output() public selectAbove = new EventEmitter<void>();
    @Output() public selectBelow = new EventEmitter<void>();
    @Output() public selectLeft = new EventEmitter<void>();
    @Output() public selectRight = new EventEmitter<void>();

    // List of all cells
    @ViewChildren('cell') public _cellViews: QueryList<CellComponent<any>>;

    public _types: TailLineType[] = [
        '€', '%', 't'
    ];

    // private subscriptions: Subscription[] = [];

    constructor(
        private undoService: UndoService,
        // private applicationDataService: ApplicationDataService, 
        private applicationContextService: ApplicationContextService,
        private store: Store<AppState>
    ) {
        // Grab tail from application data service
        // this.tail = applicationDataService.activeCalculation?.tail;
        this.store.select(selectCalculationMeta).pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(meta => {
            this.meta = meta;
        })
    }

    public ngOnInit(): void {
        // this.subscriptions = [
        //     this.applicationDataService.activeCalculationChange.subscribe(() => {
        //         // Grab tail from application data service
        //         this.tail = this.applicationDataService.activeCalculation?.tail;
        //     })
        // ];
    }

    public ngOnDestroy(): void {
        // this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    public getCellRect(columnIndex: number): Rect | undefined;
    public getCellRect(column: string): Rect | undefined;
    public getCellRect(column: string | number): Rect | undefined {
        // Get column index
        const columnIndex = typeof (column) === 'number' ? column : this.columns[column].index;

        // Get the cell
        const cell = this._cellViews.find(cell => cell.column.index === columnIndex);
        if (!cell) return undefined;

        // Return bounding rect
        return cell.getRelativeBoundingRect();
    }

    /* --- Getters & setters --- */

    public get selectionCurrentCellRect(): Rect | undefined {
        // Get the cell
        const cell = this._cellViews.find(cell => this.index === this.selection.current.row && cell.column?.index === this.selection.current.column);
        if (!cell) return undefined;

        // console.log(this.selection, cell);

        // Return bounding rect
        return cell.getRelativeBoundingRect();
    }

    public get selectionOriginCellRect(): Rect | undefined {
        if (!this.selection.origin) return undefined;

        // Get the cell
        const cell = this._cellViews.find(cell => this.index === this.selection.origin.row && cell.column?.index === this.selection.origin?.column);
        if (!cell) return undefined;

        // Return boudning rect
        return cell.getRelativeBoundingRect();
    }

    /* --- Functions --- */

    public focusCell(columnIndex: number, shallow: boolean = true) {
        // Get the cell view for the column
        const cell = this._cellViews.find(cell => {
            return cell.column ? cell.column.index === columnIndex : false;
        });

        // If a valid cell was found, focus the cell
        if (cell)
            cell.focus(shallow);
    }

    /* --- Transforms --- */

    public _isCalculationLine(line: ViewModelLine): boolean {
        return line.type === ViewModelLineType.TAIL_AMOUNT || line.type === ViewModelLineType.TAIL_PERCENTAGE;
    }

    public _isAmountLine(line: ViewModelLine): boolean {
        return line.type === ViewModelLineType.TAIL_AMOUNT;
    }

    public _getType(line: ViewModelLine): TailLineType {
        return line.type === ViewModelLineType.TAIL_AMOUNT ? '€' : line.type === ViewModelLineType.TAIL_PERCENTAGE ? '%' : 't';
    }

    /* --- Event handlers --- */

    public _HandleChangeType(type: TailLineType) {
        const oldType = this._line.type === ViewModelLineType.TAIL_AMOUNT ? 'AMOUNT' : this._line.type === ViewModelLineType.TAIL_PERCENTAGE ? 'PERCENTAGE' : 'TEXT';
        const newType = type === '€' ? 'AMOUNT' : type === '%' ? 'PERCENTAGE' : 'TEXT';

        // Create a copy of the line to send
        const updatedLine: CalculationTailApi = { ...this._line.line };
        updatedLine.type = newType;
        this.store.dispatch(updateTailLineUi({ projectId: this.meta.projectId, calculationId: this.meta.id, line: updatedLine }));

        // // Change line type
        // this._line = this.tail.changeType(this._line as EditableTailLine, newType);

        // this.undoService.addUndoRedo(() => { // Undo
        //     // Change line type
        //     this._line = this.tail.changeType(this._line as EditableTailLine, oldType);

        //     // Emit change event
        //     this.change.emit(this._line);
        // }, () => { // Redo
        //     // Change line type
        //     this._line = this.tail.changeType(this._line as EditableTailLine, newType);

        //     // Emit change event
        //     this.change.emit(this._line);
        // });
    }

    public _setPropertyUndo<K extends keyof CalculationTailApi>(property: K, newValue: CalculationTailApi[K]): void {
        if (!this.meta || !this._line?.line) {
            // console.log('FAIL TESTS');
            return;
        }

        // Create a copy of the line to send
        const updatedLine: CalculationTailApi = { ...this._line.line };
        updatedLine[property] = newValue;
        this.store.dispatch(updateTailLineUi({ projectId: this.meta.projectId, calculationId: this.meta.id, line: updatedLine }));

        // const oldValue = this._line[property];

        // this.undoService.addUndoRedo(() => { // Undo
        //     // Assign old value to property
        //     this._line[property] = oldValue;

        //     // Emit change event
        //     this.change.emit(this._line);
        // }, () => { // Redo
        //     // Re-assign new value to property
        //     this._line[property] = newValue;

        //     // Emit change event
        //     this.change.emit(this._line);
        // });
    }

    public _handleSelectionChange(selection: CalculationSelection) {
        this.selection = {
            ...selection,
            current: {
                row: this.index, // Add row here
                column: selection.current.column
            },
            selectedLines: [JSON.parse(JSON.stringify(this._line))]
        };
        this.store.dispatch(CalculationActions.updateSelection({
            selection: this.selection,
        }));
    }

    public _handleSelectAbove() {
        this.selectAbove.emit();
    }

    public _handleSelectBelow() {
        this.selectBelow.emit();
    }

    public _handleSelectLeft() {
        this.selectLeft.emit();
    }

    public _handleSelectRight() {
        this.selectRight.emit();
    }
}
